import {
  ComponentBox,
  ComponentBoxHeaderFooter,
  EmptyResult,
  intlShape,
  LoadingSpinner,
  utils,
} from '@wallaby-ui/core';
import classNames from 'classnames';
import {map} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import {defineMessages, FormattedMessage, injectIntl} from 'react-intl';
import PropertiesGroup from 'src/js/containers/document/components/PropertiesGroup';
import {PropertiesMap} from 'src/js/containers/document/components/PropertiesMap';
import {DocumentLink} from 'src/js/containers/file/components/DocumentLink';
import {FileDownload} from 'src/js/containers/file/components/FileDownload';
import {getTableColumnsDataGridProjection} from 'src/js/containers/grid/grid_projection';
import {getReducers} from 'src/js/containers/grid/grid_reducers';
import {DocumentConfig} from 'src/js/model/document_config';
import {File as FileModel} from 'src/js/model/file';

import styles from './File.less';

export const I18N = defineMessages({
  OPEN_DOCUMENT: {
    id: 'file.openDocument',
    defaultMessage: 'Open document',
    description: 'title for document link',
  },
  TITLE: {
    id: 'file.title',
    defaultMessage: 'FILE ATTRIBUTES',
    description: 'title text for file box',
  },
  HELP: {
    id: 'file.help',
    defaultMessage: 'This box is showing the file attributes.',
    description: 'help text for file box',
  },
  DOWNLOAD: {
    id: 'file.download',
    defaultMessage: 'Download file',
    description: 'title text for file download button',
  },
});

const gridIds = {
  FILE_OTHER_FORMATS: 'FILE_OTHER_FORMATS',
  FILE_HIERARCHIES: 'FILE_HIERARCHIES',
  FILE_LINKED_FILES: 'FILE_LINKED_FILES',
};

class File extends React.PureComponent {
  constructor(props) {
    super(props);

    this.reducers = {
      [gridIds.FILE_HIERARCHIES]: getReducers(gridIds.FILE_HIERARCHIES),
      [gridIds.FILE_LINKED_FILES]: getReducers(gridIds.FILE_LINKED_FILES),
      [gridIds.FILE_OTHER_FORMATS]: getReducers(gridIds.FILE_OTHER_FORMATS),
    };
  }

  render() {
    const {
      config,
      context,
      currentFile,
      documentDetailRoute,
      intl,
      isLoadingCounter,
      loadingDisabled,
      resultDetailConfig,
      resultDetailProjectConfig,
      showBoxHeader,
      showOpenDocumentButton,
      tablesConfig,
    } = this.props;
    const linkedFilesConfig =
      tablesConfig[config.service.url.documentLinkedFilesTableConfig];
    const otherFormatsConfig =
      tablesConfig[config.service.url.documentOtherFormatsTableConfig];
    const hierarchiesConfig =
      tablesConfig[config.service.url.documentHierarchiesTableConfig];
    const isLoading = !loadingDisabled && isLoadingCounter > 0;

    return (
      <div
        className={classNames(styles.dlFile, 'dlLoadingSpinnerContainer', {
          dlLoadingSpinnerParent: isLoading,
        })}
      >
        <LoadingSpinner isVisible={isLoading} text='document.loading' />
        {showBoxHeader || showOpenDocumentButton ? (
          <ComponentBoxHeaderFooter
            titleText={utils.TranslateUtils.translate(I18N.TITLE, intl)}
            helpText={utils.TranslateUtils.translate(I18N.HELP, intl)}
            showBorderBottom={false}
            showHelp={false}
            showTitle={false}
          >
            <span className={classNames('float-end', styles.dlFileButton)}>
              {showOpenDocumentButton &&
              currentFile &&
              currentFile.getDocumentId() ? (
                <DocumentLink
                  docId={currentFile.getDocumentId()}
                  documentDetailRoute={documentDetailRoute}
                  titleText={utils.TranslateUtils.translate(
                    I18N.OPEN_DOCUMENT,
                    intl
                  )}
                >
                  <i
                    role={'button'}
                    title={utils.TranslateUtils.translate(
                      I18N.OPEN_DOCUMENT,
                      intl
                    )}
                    className={'far fa-file-alt'}
                  />
                </DocumentLink>
              ) : null}
            </span>
          </ComponentBoxHeaderFooter>
        ) : null}
        <ComponentBox hasScrollbar>
          {currentFile && currentFile.getId() ? (
            <div className={styles.dlFileData} key={currentFile.getId()}>
              <div className={styles.dlFileAttributes}>
                <div key={'fileDownload'} className={styles.dlFileAttribute}>
                  <span
                    className={styles.dlFileAttributeLabel}
                    title={utils.TranslateUtils.translate(I18N.DOWNLOAD, intl)}
                  >
                    <span>
                      <FormattedMessage {...I18N.DOWNLOAD} />
                      {':'}
                    </span>
                  </span>
                  <span className={styles.dlFileAttributeValue}>
                    <FileDownload
                      downloadUrl={FileModel.createDownloadUrl(
                        config,
                        currentFile.getBlobId()
                      )}
                      titleText={utils.TranslateUtils.translate(
                        I18N.DOWNLOAD,
                        intl
                      )}
                    >
                      <i className={'fas fa-download'} />
                    </FileDownload>
                  </span>
                </div>
                <PropertiesGroup
                  title={utils.TranslateUtils.translate(
                    DocPropertiesI18N.PROJECT_LABEL,
                    intl
                  )}
                >
                  <PropertiesMap
                    dataObject={currentFile.getProjectProperties()}
                    propertiesConfigMap={resultDetailProjectConfig.getPropertiesMapByView()}
                    mapByConfig={false}
                    showBorders={false}
                  />
                </PropertiesGroup>

                <PropertiesGroup
                  title={utils.TranslateUtils.translate(
                    DocPropertiesI18N.LABEL,
                    intl
                  )}
                >
                  <PropertiesMap
                    dataObject={currentFile.getRawData()}
                    propertiesConfig={resultDetailConfig.getPropertiesByView()}
                    showBorders={false}
                  />
                </PropertiesGroup>
              </div>
              {currentFile.getHierarchies().length > 0 && hierarchiesConfig ? (
                <div className={styles.dlFileArrayAttributes}>
                  <div className={styles.dlFileArrayAttributeLabel}>
                    Hierarchies:
                  </div>
                  <div
                    className={classNames(
                      styles.dlFileArrayAttributeValue,
                      styles.dlFileArrayAttributeValueMinHeight
                    )}
                  >
                    <AgGridContainer
                      maxHeight={300}
                      columns={getTableColumnsDataGridProjection(
                        hierarchiesConfig.getColumns()
                      )}
                      context={context}
                      id={gridIds.FILE_HIERARCHIES}
                      reducers={this.reducers[gridIds.FILE_HIERARCHIES]}
                      rows={map(currentFile.getHierarchies(), (item) =>
                        item.getTableProjection()
                      )}
                    />
                  </div>
                </div>
              ) : null}
              {currentFile.getOtherFormats().length > 0 &&
              otherFormatsConfig ? (
                <div className={styles.dlFileArrayAttributes}>
                  <div className={styles.dlFileArrayAttributeLabel}>
                    {'Other formats:'}
                  </div>
                  <div
                    className={classNames(
                      styles.dlFileArrayAttributeValue,
                      styles.dlFileArrayAttributeValueMinHeight
                    )}
                  >
                    <AgGridContainer
                      maxHeight={300}
                      columns={getTableColumnsDataGridProjection(
                        otherFormatsConfig.getColumns()
                      )}
                      context={context}
                      id={gridIds.FILE_OTHER_FORMATS}
                      reducers={this.reducers[gridIds.FILE_OTHER_FORMATS]}
                      rows={map(currentFile.getOtherFormats(), (item) =>
                        item.getTableProjection(config)
                      )}
                    />
                  </div>
                </div>
              ) : null}
              {currentFile.getLinkedFiles().length > 0 && linkedFilesConfig ? (
                <div className={styles.dlFileArrayAttributes}>
                  <div className={styles.dlFileArrayAttributeLabel}>Links:</div>
                  <div
                    className={classNames(
                      styles.dlFileArrayAttributeValue,
                      styles.dlFileArrayAttributeValueMinHeight
                    )}
                  >
                    <AgGridContainer
                      maxHeight={300}
                      columns={getTableColumnsDataGridProjection(
                        linkedFilesConfig.getColumns()
                      )}
                      context={context}
                      id={gridIds.FILE_LINKED_FILES}
                      reducers={this.reducers[gridIds.FILE_LINKED_FILES]}
                      rows={map(currentFile.getLinkedFiles(), (item) =>
                        item.getTableProjection(config)
                      )}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <EmptyResult />
          )}
        </ComponentBox>
      </div>
    );
  }
}

File.displayName = 'File';

File.propTypes = {
  config: PropTypes.objectOf(PropTypes.any).isRequired,
  context: PropTypes.oneOfType([
    PropTypes.objectOf(PropTypes.any),
    PropTypes.string,
  ]),
  currentFile: PropTypes.instanceOf(FileModel),
  documentDetailRoute: PropTypes.string.isRequired,
  resultDetailConfig: PropTypes.instanceOf(DocumentConfig).isRequired,
  resultDetailProjectConfig: PropTypes.instanceOf(DocumentConfig).isRequired,
  intl: intlShape.isRequired,
  isLoadingCounter: PropTypes.number.isRequired,
  loadingDisabled: PropTypes.bool,
  showBoxHeader: PropTypes.bool,
  showOpenDocumentButton: PropTypes.bool,
  tablesConfig: PropTypes.objectOf(PropTypes.any).isRequired,
};

File.defaultProps = {
  context: undefined,
  currentFile: undefined,
  loadingDisabled: false,
  showBoxHeader: true,
  showOpenDocumentButton: true,
};

export default injectIntl(File);
